import React, { useState, type ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import ChevronDown from '@atlaskit/icon/glyph/hipchat/chevron-down';
import ChevronUp from '@atlaskit/icon/glyph/hipchat/chevron-up';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize, borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export type CollapsibleGroupProps = {
	isDefaultCollapsed: boolean;
	title: string;
	children: ReactNode;
	headerItem?: ReactNode;
	showHeaderItemWhenOpen: boolean;
	testId?: string;
	titleCanWrap?: boolean;
	headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
};

export const CollapsibleGroup = (props: CollapsibleGroupProps) => {
	const {
		children,
		title,
		headerItem,
		isDefaultCollapsed,
		showHeaderItemWhenOpen,
		testId,
		titleCanWrap = false,
		headingLevel = 'h3',
	} = props;
	const bodyTestId = testId != null && `${testId}.body`;
	const headerTestId = testId != null && `${testId}.header`;
	const [isOpen, setIsOpen] = useState<boolean>(!isDefaultCollapsed);

	const shouldRenderHeaderItem = headerItem != null && (showHeaderItemWhenOpen || !isOpen);

	const toggleIsOpen = () => setIsOpen((open) => !open);

	if (fg('jsm_cmdb_import_options_return_focus_on_cancel')) {
		return (
			<div data-testid={props.testId}>
				<Box as={headingLevel}>
					<Header
						data-testid={headerTestId}
						isOpen={isOpen}
						onClick={toggleIsOpen}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								toggleIsOpen();
							}
						}}
						aria-expanded={isOpen}
					>
						<Box as="span" xcss={titleCanWrap ? headingTitleWrapStyle : headingTitleNoWrapStyle}>
							{title}
						</Box>
						{shouldRenderHeaderItem && (
							<Box
								as="span"
								xcss={headerItemStyles}
								testId="servicedesk-insight-shared-ui.ui.collapsible-group.header-item"
							>
								{headerItem}
							</Box>
						)}
						{isOpen ? <ChevronUp label="" /> : <ChevronDown label="" />}
					</Header>
				</Box>
				{isOpen ? (
					<Box
						xcss={bodyStyles}
						testId={bodyTestId || 'servicedesk-insight-shared-ui.ui.collapsible-group.body'}
					>
						{children}
					</Box>
				) : null}
			</div>
		);
	}

	return (
		<div data-testid={props.testId}>
			<Header
				data-testid={headerTestId}
				isOpen={isOpen}
				onClick={toggleIsOpen}
				onKeyDown={(event) => {
					if (event.key === 'Enter') {
						toggleIsOpen();
					}
				}}
				aria-expanded={isOpen}
			>
				<Title canWrap={titleCanWrap || false}>{title}</Title>
				{shouldRenderHeaderItem && (
					<HeaderItem data-testid="servicedesk-insight-shared-ui.ui.collapsible-group.header-item">
						{headerItem}
					</HeaderItem>
				)}
				{isOpen ? <ChevronUp label="" /> : <ChevronDown label="" />}
			</Header>
			<Body data-testid={bodyTestId} isOpen={isOpen}>
				{children}
			</Body>
		</div>
	);
};

CollapsibleGroup.defaultProps = {
	isDefaultCollapsed: false,
	showHeaderItemWhenOpen: false,
};

const textOverflow = css({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Body = styled.div<{ isOpen: boolean }>(
	{
		padding: token('space.200', '16px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isOpen }) =>
		isOpen
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					border: `1px solid ${token('color.border', colors.N40)}`,
					borderTop: 0,
				})
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				css({
					display: 'none',
				}),
);

const bodyStyles = xcss({
	padding: 'space.200',
	borderWidth: '1px',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderTop: 'none',
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.strong<{ canWrap: boolean }>`
	${
		(props: any) => (props.canWrap ? '' : textOverflow) // eslint-disable-line @typescript-eslint/no-explicit-any
	};
	color: ${token('color.text', colors.N800)};
	padding-right: ${token('space.050', '4px')};
	font-size: 16px;
	max-width: 60%;
	font-weight: 600;
	flex-shrink: 0;
`;

// FIXME: type error passing result of `css` to `styled`
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderItem = styled.span`
	${textOverflow};
	margin-left: auto;
	color: ${token('color.text.subtlest', colors.N90)};
	padding: ${token('space.050', '4px')};
	margin-right: ${token('space.100', '8px')};
`;

const headerItemStyles = xcss({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	color: 'color.text.subtlest',
	padding: 'space.050',
	marginRight: 'space.100',
	font: 'font.body',
	marginLeft: 'auto',
});

// Using a div[role=button] instead of an actual button element so that we don't
// end up with nested buttons when injecting a button as a headerItem prop
// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div.attrs<{ isOpen: boolean }>({
	role: 'button',
	tabIndex: 0,
})`
	display: flex;
	align-items: center;
	justify-content: space-between;

	height: ${gridSize * 7}px;
	padding: 0 ${gridSize * 2}px;
	border-radius: ${borderRadius}px;
	border: 1px solid ${token('color.border', colors.N40)};

	&:hover {
		background: ${token('color.background.neutral.subtle.hovered', colors.N20)};
		cursor: pointer;
	}

	&:focus {
		outline: none;
		box-shadow: none;
		border-color: ${token('color.border.selected', colors.B300)};
		background: ${token('color.background.neutral.subtle.hovered', colors.N20)};
	}

	&:active {
		background-color: ${token('color.background.selected', colors.B50)};
		border-color: ${token('color.border.selected', colors.B300)};
	}

	${({ isOpen }) =>
		isOpen
			? css({
					borderBottomRightRadius: 0,
					borderBottomLeftRadius: 0,
				})
			: ''}
`;

const headingTitleWrapStyle = xcss({
	color: 'color.text',
	font: 'font.heading.small',
	paddingRight: 'space.050',
	width: '100%',
	maxWidth: '60%',
	fontWeight: 600,
	flexShrink: '0',
});

const headingTitleNoWrapStyle = xcss({
	color: 'color.text',
	paddingRight: 'space.050',
	font: 'font.heading.small',
	width: '100%',
	maxWidth: '60%',
	fontWeight: 600,
	flexShrink: '0',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
